import React, { useState, useMemo, useEffect } from "react";
import { Pagination, Button } from "antd";
import { useTranslation } from "react-i18next";
import CampaignsManagerHolder from "../../styles/campaignsManagerStyle";
import useCampaignsList from "../../hooks/useCampaignsList";
import useCampaignActions from "../../hooks/useCampaignActions";
import useViewMode from "../../hooks/useViewMode";
import { sortCampaigns } from "../../utils/sortCampaigns";
import { CAMPAIGN_STATES, PROPOSAL_STATES } from "../../utils/campaignFilters";
import CampaignsTabs from "./CampaignsTabs";
import CampaignsToolbar from "./CampaignsToolbar";
import CampaignsFilters from "./CampaignsFilters";
import CampaignsGrid from "./CampaignsGrid";

const CampaignsManager = (props) => {
  const { t } = useTranslation();
  const variant = props.variant === "proposals" ? "proposals" : "campaigns";
  const isProposals = variant === "proposals";

  const [viewMode, setViewMode] = useViewMode("grid");
  const [sortValue, setSortValue] = useState("recent");
  const [filtersVisible, setFiltersVisible] = useState(true);

  useEffect(() => {
    if (!props.user) {
      if (props.setShouldPromptLogin) {
        props.setShouldPromptLogin(true, window.location.pathname);
      }
      if (props.history) props.history.push("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    tabs,
    activeTab,
    search,
    filters,
    page,
    length,
    total,
    campaigns,
    loading,
    changeTab,
    changePage,
    onSearchChange,
    applyFilters,
  } = useCampaignsList({
    user: props.user,
    campaigns: props.campaigns,
    totalCampaigns: props.totalCampaigns,
    fetching: props.fetching,
    pageCampaigns: props.pageCampaigns,
    getCampaigns: props.getCampaigns,
    setPageCampaigns: props.setPageCampaigns,
    states: isProposals ? PROPOSAL_STATES : CAMPAIGN_STATES,
  });

  const { goToDetail, goToEdit, requestArchive, requestDelete } = useCampaignActions({
    history: props.history,
    deleteCampaign: props.deleteCampaign,
    archiveCampaign: props.archiveCampaign,
    loadingDeleteCampaign: props.loadingDeleteCampaign,
    loadingArchiveCampaign: props.loadingArchiveCampaign,
    errors: props.errors,
  });

  const currentTab = tabs.find((tb) => String(tb.tab) === String(activeTab));
  const state = currentTab ? currentTab.value : "active";
  const statusLabel = t(currentTab ? currentTab.labelKey : "Active");

  const sortedCampaigns = useMemo(
    () => sortCampaigns(campaigns, sortValue),
    [campaigns, sortValue]
  );

  const filteredCampaigns = useMemo(() => {
    return sortedCampaigns.filter((c) => {
      if (filters.clientId && (!c.client || c.client.id !== filters.clientId)) {
        return false;
      }
      if (filters.dateFrom || filters.dateTo) {
        const cStart = new Date(c.start_date);
        const cEnd = new Date(c.end_date);
        if (filters.dateFrom && cEnd < new Date(`${filters.dateFrom}T00:00:00`)) {
          return false;
        }
        if (filters.dateTo && cStart > new Date(`${filters.dateTo}T23:59:59`)) {
          return false;
        }
      }
      return true;
    });
  }, [sortedCampaigns, filters]);

  const clientOptions = useMemo(() => {
    const map = new Map();
    (props.campaigns || []).forEach((c) => {
      if (c.client && c.client.id && !map.has(c.client.id)) {
        map.set(c.client.id, { id: c.client.id, name: c.client.name });
      }
    });
    return Array.from(map.values());
  }, [props.campaigns]);

  const goToCreate = () => {
    if (props.history) props.history.push("/campaignManager");
  };

  return (
    <CampaignsManagerHolder>
      <div className="campaignsManager__header">
        <h1 className="campaignsManager__title">
          {isProposals ? t("My proposals") : t("My campaigns")}
        </h1>
        <Button type="primary" icon="plus" onClick={goToCreate}>
          {isProposals ? t("New proposal") : t("New campaign")}
        </Button>
      </div>

      <CampaignsTabs tabs={tabs} activeTab={activeTab} onChange={changeTab} />

      <CampaignsToolbar
        search={search}
        onSearchChange={onSearchChange}
        sortValue={sortValue}
        onSortChange={setSortValue}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onToggleFilters={() => setFiltersVisible((v) => !v)}
      />

      {filtersVisible && (
        <CampaignsFilters clientOptions={clientOptions} onApply={applyFilters} />
      )}

      <CampaignsGrid
        campaigns={filteredCampaigns}
        loading={loading}
        viewMode={viewMode}
        variant={variant}
        state={state}
        statusLabel={statusLabel}
        onDetail={goToDetail}
        onEdit={goToEdit}
        onDelete={requestDelete}
        onArchive={requestArchive}
      />

      {total > length && (
        <div className="campaignsManager__pagination">
          <Pagination
            current={page}
            total={total}
            pageSize={length}
            onChange={changePage}
          />
        </div>
      )}
    </CampaignsManagerHolder>
  );
};

export default CampaignsManager;
